import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { ImagePlus, Replace, TriangleAlert } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import IconButton from "../../../components/Buttons/IconButton";
import ModalMedia from "../../../components/Modal/ModalMedia";
import ModalGalleryAssets from "../../../components/Modal/ModalGalleryAssets";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_PRODUCE_IMAGE_CHECKING,
  TEMPLATES_PRODUCE_IMAGE_UNUSABLE,
  TEMPLATES_PRODUCE_REPLACE_IMAGE,
  TEMPLATES_PRODUCE_SELECT_IMAGE,
} from "../../../i18n/keysTranslations";

const iconButtonSx = {
  width: 32,
  height: 32,
  color: "text.primary",
  "&:hover": { color: "text.primary" },
};

const ProduceImageCell = ({
  url = "",
  status = "ok",
  onChange = () => {},
  disabled = false,
  selectAsButton = false,
}) => {
  const { t } = useTranslationApp();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const hasImage = Boolean(url);
  let statusTooltip = "";
  switch (status) {
    case "checking":
      statusTooltip = t(TEMPLATES_PRODUCE_IMAGE_CHECKING);
      break;
    case "error":
      statusTooltip = t(TEMPLATES_PRODUCE_IMAGE_UNUSABLE);
      break;
    case "ok":
      break;
    default: {
      const exhaustive = status;
      void exhaustive;
    }
  }
  const isChecking = status === "checking";
  const isError = status === "error";

  const onOpenPreview = (event) => {
    event?.stopPropagation?.();
    if (isChecking) return;
    setIsPreviewOpen(true);
  };

  const onOpenGallery = (event) => {
    event?.stopPropagation?.();
    if (disabled) return;
    setIsGalleryOpen(true);
  };

  const onSelectGalleryItems = (selectedItems) => {
    const nextUrl = selectedItems?.[0]?.url || "";
    if (nextUrl) onChange(nextUrl);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {hasImage ? (
        <>
          <Tooltip title={statusTooltip} disableHoverListener={!statusTooltip}>
            <Box
              onClick={onOpenPreview}
              sx={{
                position: "relative",
                width: 48,
                height: 48,
                flexShrink: 0,
                cursor: isChecking ? "default" : "pointer",
              }}
            >
              <Box
                component="img"
                src={url}
                alt=""
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: "8px",
                  pointerEvents: "none",
                  backgroundColor: "#EEE",
                  opacity: isChecking || isError ? 0.55 : 1,
                  outline: isError ? "2px solid" : "none",
                  outlineColor: isError ? "error.main" : "transparent",
                }}
              />
              {isChecking || isError ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    pointerEvents: "none",
                    backgroundColor: "rgba(255, 255, 255, 0.45)",
                  }}
                >
                  {isChecking ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <TriangleAlert size={16} color="#B91C1C" />
                  )}
                </Box>
              ) : null}
            </Box>
          </Tooltip>
          <IconButton
            tooltipText={
              isError
                ? TEMPLATES_PRODUCE_IMAGE_UNUSABLE
                : TEMPLATES_PRODUCE_REPLACE_IMAGE
            }
            disabled={disabled}
            onClick={onOpenGallery}
            sx={iconButtonSx}
          >
            <Replace size={16} />
          </IconButton>
        </>
      ) : selectAsButton ? (
        <Button
          color="secondary"
          type="button"
          disabled={disabled}
          onClick={onOpenGallery}
          sx={{ height: 36, minWidth: 0, px: 1.5 }}
        >
          {t(TEMPLATES_PRODUCE_SELECT_IMAGE)}
        </Button>
      ) : (
        <IconButton
          tooltipText={TEMPLATES_PRODUCE_SELECT_IMAGE}
          disabled={disabled}
          onClick={onOpenGallery}
          sx={iconButtonSx}
        >
          <ImagePlus size={18} />
        </IconButton>
      )}
      {isGalleryOpen ? (
        <ModalGalleryAssets
          modalOpen={isGalleryOpen}
          multiple={false}
          onCloseModal={() => setIsGalleryOpen(false)}
          onCallbackSelectedItems={onSelectGalleryItems}
        />
      ) : null}
      {isPreviewOpen ? (
        <ModalMedia
          modalOpen={isPreviewOpen}
          onCloseModal={() => setIsPreviewOpen(false)}
          url={url}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton={false}
          isPicker={true}
        />
      ) : null}
    </Box>
  );
};

export default ProduceImageCell;
